import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowsLeftRight, Copy, ArrowsClockwise, Trash, CaretDown, Plus } from "@phosphor-icons/react";

export default function CalendarSync({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState({ url: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/calendar/feed-url").then((r) => setFeedUrl(r.data.url)).catch(() => {});
    loadSubs();
  }, [open]); // eslint-disable-line

  const loadSubs = () => api.get("/calendar/subscriptions").then((r) => setSubs(r.data)).catch(() => {});

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать — выделите и скопируйте вручную");
    }
  };

  const regen = async () => {
    const { data } = await api.post("/calendar/feed-url/regenerate");
    setFeedUrl(data.url);
    toast.success("Ссылка обновлена — старая больше не работает");
  };

  const addSub = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/calendar/subscriptions", { url: form.url, name: form.name });
      toast.success(`Календарь подключён: ${data.events_count} событий`);
      setForm({ url: "", name: "" });
      loadSubs();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const refreshSub = async (id) => {
    const { data } = await api.post(`/calendar/subscriptions/${id}/refresh`);
    toast.success(`Обновлено: ${data.events_count} событий`);
    loadSubs();
    onChanged?.();
  };

  const removeSub = async (id) => {
    await api.delete(`/calendar/subscriptions/${id}`);
    loadSubs();
    onChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="calendar-sync-button">
          <ArrowsLeftRight size={15} className="mr-1.5" /> Синхронизация
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-modal w-full sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Синхронизация календарей</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-sm font-semibold">📤 Экспорт: события LifeOS в вашем календаре</div>
          <p className="text-xs text-muted-foreground">Добавьте эту секретную ссылку в Apple, Google или Outlook — события LifeOS появятся там и будут обновляться автоматически.</p>
          <div className="flex gap-2">
            <Input readOnly value={feedUrl} className="text-xs" data-testid="feed-url-input" />
            <Button size="sm" variant="outline" onClick={copy} data-testid="feed-copy-button"><Copy size={14} /></Button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowHelp((s) => !s)} data-testid="feed-help-toggle"
              className="flex items-center gap-1 text-xs text-primary hover:underline">
              Как добавить в Apple / Google? <CaretDown size={12} className={`transition-transform ${showHelp ? "rotate-180" : ""}`} />
            </button>
            <button onClick={regen} data-testid="feed-regen-button" className="text-xs text-muted-foreground hover:text-destructive transition-colors">
              Сбросить ссылку
            </button>
          </div>
          {showHelp && (
            <div className="text-xs text-muted-foreground space-y-2 rounded-md bg-white/[0.04] border border-white/[0.07] p-3" data-testid="feed-help">
              <p><span className="text-foreground font-semibold">iPhone / Apple Calendar:</span> Настройки → Приложения → Календарь → Учётные записи → Добавить → Другое → «Подписной календарь» → вставьте ссылку.</p>
              <p><span className="text-foreground font-semibold">Google Calendar:</span> на calendar.google.com слева «Другие календари» → «+» → «По URL» → вставьте ссылку.</p>
              <p><span className="text-foreground font-semibold">Outlook:</span> Добавить календарь → «Подписаться из Интернета».</p>
            </div>
          )}
        </div>

        <div className="space-y-2 pt-3 border-t border-white/[0.07]">
          <div className="text-sm font-semibold">📥 Импорт: внешние календари в LifeOS</div>
          <p className="text-xs text-muted-foreground">Вставьте ICS-ссылку календаря (iCloud: «Открытый календарь»; Google: настройки календаря → «Закрытый адрес в формате iCal»).</p>
          <form onSubmit={addSub} className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Название</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Рабочий календарь" required data-testid="sub-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ссылка (ICS / webcal)</Label>
              <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://… или webcal://…" required data-testid="sub-url-input" />
            </div>
            <Button type="submit" size="sm" disabled={busy} className="w-full" data-testid="sub-submit-button">
              <Plus size={14} className="mr-1" /> {busy ? "Подключаем…" : "Подключить календарь"}
            </Button>
          </form>
          {subs.length > 0 && (
            <div className="space-y-1.5 pt-1" data-testid="subs-list">
              {subs.map((s) => (
                <div key={s.id} className="p-2 rounded-md bg-white/[0.04] border border-white/[0.06]" data-testid={`sub-item-${s.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-[#a970ff] shrink-0" />
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="text-xs text-muted-foreground num shrink-0">{s.events_count} соб.</span>
                    <button onClick={() => refreshSub(s.id)} data-testid={`sub-refresh-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors"><ArrowsClockwise size={14} /></button>
                    <button onClick={() => removeSub(s.id)} data-testid={`sub-delete-${s.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={14} /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 pl-4">
                    {s.synced_at ? (
                      <span className="text-[10px] text-muted-foreground/70">обновлено {new Date(s.synced_at * 1000).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    ) : null}
                    {s.last_error && <span className="text-[10px] text-destructive" data-testid={`sub-error-${s.id}`}>{s.last_error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
